import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/* NIKA (16.09.2026): смяната на вариант тегли ЦЯЛАТА продуктова страница (~1MB, 400-700ms
   при първо теглене). Кеш в паметта (5 мин) + предварително теглене при hover/touch върху
   кутийката → повторните и „подготвените“ смени са мигновени. Споделено между заявките:
   prefetch-ът не се прекъсва от abort-а на потребителската заявка. */
const NIKA_VP_CACHE = new Map();
const NIKA_VP_INFLIGHT = new Map();
const NIKA_VP_TTL = 5 * 60 * 1000;
function nikaVpGet(url) {
  const e = NIKA_VP_CACHE.get(url);
  if (!e) return null;
  if (Date.now() - e.t > NIKA_VP_TTL) { NIKA_VP_CACHE.delete(url); return null; }
  return e.text;
}
function nikaVpFetchText(url, signal) {
  const cached = nikaVpGet(url);
  if (cached !== null) return Promise.resolve(cached);
  let p = NIKA_VP_INFLIGHT.get(url);
  if (!p) {
    p = fetch(url).then((r) => r.text()).then((t) => { NIKA_VP_CACHE.set(url, { t: Date.now(), text: t }); NIKA_VP_INFLIGHT.delete(url); return t; })
      .catch((e) => { NIKA_VP_INFLIGHT.delete(url); throw e; });
    NIKA_VP_INFLIGHT.set(url, p);
  }
  if (!signal) return p;
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('aborted', 'AbortError'));
    const onAbort = () => reject(new DOMException('aborted', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    p.then((t) => { signal.removeEventListener('abort', onAbort); resolve(t); }, (e) => { signal.removeEventListener('abort', onAbort); reject(e); });
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    /* NIKA: prefetch на варианта при посочване/докосване на кутийка */
    this.addEventListener('pointerover', this.#nikaPrefetch);
    this.addEventListener('touchstart', this.#nikaPrefetch, { passive: true });
    this.#nikaLoadVariants();
    if ('requestIdleCallback' in window) requestIdleCallback(() => this.#nikaIdlePrefetch(), { timeout: 3000 });
    else setTimeout(() => this.#nikaIdlePrefetch(), 1500);
  }

  /** NIKA: тегли предварително страницата за варианта под мишката/пръста (само главният picker) */
  #nikaPrefetch = (event) => {
    try {
      const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
      if (!label) return;
      const input = label.querySelector('input');
      if (!input || input.checked || input.disabled || input.dataset.connectedProductUrl) return;
      if (this.closest('quick-add-component, swatches-variant-picker-component, featured-product-information')) return;
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      const idx = fieldsets.indexOf(input.closest('fieldset'));
      if (idx < 0) return;
      const vals = this.selectedOptionsValues.slice();
      if (vals.length !== fieldsets.length) return;
      vals[idx] = input.dataset.optionValueId || '';
      if (!vals[idx]) return;
      const url = this.#nikaUrlFor(vals, input);
      if (url && nikaVpGet(url) === null && !NIKA_VP_INFLIGHT.has(url)) nikaVpFetchText(url).catch(() => {});
    } catch (e) { /* без prefetch */ }
  };

  /** NIKA: данните на вариантите (/products/<handle>.js, ~30 KB) — теглят се веднъж след зареждане */
  #nikaVariants = null;
  #nikaLoadVariants() {
    if (this.#nikaVariants || !this.#nikaMainSectionId(null)) return;
    const url = (this.dataset.productUrl || '').split('?')[0];
    if (!url) return;
    fetch(url + '.js').then((r) => r.json()).then((j) => { if (j && Array.isArray(j.variants)) this.#nikaVariants = j.variants; }).catch(() => {});
  }

  /** NIKA: подменя текста на цената (и compare-at + "Спестявате") веднага, ако форматът съвпада точно */
  #nikaOptimisticPrice(newVariantId) {
    try {
      if (!this.#nikaVariants) return;
      const details = this.closest('.product-details') || this.closest('.shopify-section') || document;
      const pp = details.querySelector('product-price');
      if (!pp) return;
      const curJson = this.querySelector('script[type="application/json"]');
      const cur = curJson ? JSON.parse(curJson.textContent) : null;
      const oldV = cur && this.#nikaVariants.find((v) => String(v.id) === String(cur.id));
      const newV = this.#nikaVariants.find((v) => String(v.id) === String(newVariantId));
      if (!oldV || !newV || oldV.id === newV.id) return;
      const fmt = (c) => (c / 100).toFixed(2).replace('.', ',') + ' €';
      const walker = document.createTreeWalker(pp, NodeFilter.SHOW_TEXT);
      const nodes = []; let n;
      while ((n = walker.nextNode())) nodes.push(n);
      const swap = (from, to) => { let done = false; nodes.forEach((t) => { if (t.textContent.trim() === from) { t.textContent = t.textContent.replace(from, to); done = true; } }); return done; };
      if (!swap(fmt(oldV.price), fmt(newV.price))) return;
      if (oldV.compare_at_price && newV.compare_at_price && oldV.compare_at_price > oldV.price && newV.compare_at_price > newV.price) {
        swap(fmt(oldV.compare_at_price), fmt(newV.compare_at_price));
        swap('Спестявате ' + fmt(oldV.compare_at_price - oldV.price), 'Спестявате ' + fmt(newV.compare_at_price - newV.price));
      }
    } catch (e) { /* morph-ът ще оправи */ }
  }

  /** NIKA: същият URL, който buildRequestUrl би направил за този набор от option values */
  #nikaUrlFor(vals, selectedOption) {
    const params = [];
    const view = getViewParameterValue();
    if (view) params.push(`view=${view}`);
    params.push(`option_values=${vals.join(',')}`);
    const sec = this.#nikaMainSectionId(selectedOption);
    if (sec) params.push(`section_id=${sec}`);
    return `${this.dataset.productUrl}?${params.join('&')}`;
  }

  /** NIKA (16.09.2026): след зареждане тегли тихо, една по една, алтернативите на всяка опция
      (с останалите опции както са избрани) → и ПЪРВОТО докосване е мигновено. Само на
      продуктовата страница, до 12 варианта, не при "пести данни". */
  #nikaIdlePrefetch() {
    try {
      if (!this.#nikaMainSectionId(null)) return;
      if (navigator.connection && navigator.connection.saveData) return;
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      if (!fieldsets.length) return;
      const current = this.selectedOptionsValues.slice();
      if (current.length !== fieldsets.length) return;
      const urls = [];
      fieldsets.forEach((fs, idx) => {
        fs.querySelectorAll('input').forEach((input) => {
          if (input.checked || input.disabled || input.dataset.connectedProductUrl || !input.dataset.optionValueId) return;
          const vals = current.slice(); vals[idx] = input.dataset.optionValueId;
          urls.push(this.#nikaUrlFor(vals, input));
        });
      });
      if (!urls.length || urls.length > 12) return;
      let i = 0;
      const next = () => {
        if (i >= urls.length || !this.isConnected) return;
        const u = urls[i++];
        const p = (nikaVpGet(u) !== null || NIKA_VP_INFLIGHT.has(u)) ? Promise.resolve() : nikaVpFetchText(u).catch(() => {});
        p.then(() => setTimeout(next, 150));
      };
      const start = () => setTimeout(next, 1200);
      if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
    } catch (e) { /* без prefetch */ }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    /* NIKA (16.09.2026): цената се сменя МИГНОВЕНО от данните на вариантите (като в Warehouse);
       останалото (наличност, галерия, бутони) идва с morph-а след секундa/част от секундата */
    if (!connectedProductUrl && variantId) this.#nikaOptimisticPrice(variantId);
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
        if (isOnProductPage) nikaSyncCanonical(variantId);
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    /* NIKA (16.09.2026): на продуктовата страница теглим САМО главната секция
       (~176 KB вместо ~930 KB цяла страница) — всичко, което се сменя (цена, наличност,
       галерия, бутони, таймер), е в нея. Combined listings (друг продукт) остават на цяла страница. */
    const nikaSection = this.#nikaMainSectionId(selectedOption);
    if (nikaSection) params.push(`section_id=${nikaSection}`);
    return `${productUrl}?${params.join('&')}`;
  }

  /** NIKA: id на главната продуктова секция, когда picker-ът е в нея и не сменя продукта */
  #nikaMainSectionId(selectedOption) {
    if (this.dataset.templateProductMatch !== 'true') return '';
    if (selectedOption && selectedOption.dataset && selectedOption.dataset.connectedProductUrl) {
      const cur = (this.dataset.productUrl || '').split('?')[0];
      if (selectedOption.dataset.connectedProductUrl !== cur) return '';
    }
    if (this.closest('product-card, quick-add-dialog, quick-add-component, featured-product-information')) return '';
    const sec = this.closest('.shopify-section');
    const id = sec ? sec.id.replace(/^shopify-section-/, '') : '';
    return /^template--/.test(id) ? id : '';
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    nikaVpFetchText(requestUrl, this.#abortController.signal)
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            const nikaSelectedVariant = (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null;

            deferredEventPromise.resolve({
              variant: nikaSelectedVariant,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            /* NIKA: URL-ът да сочи РЕАЛНО избрания вариант. Кликнатата опция
               носи variantId ПРЕДИ авто-корекцията на несъвместима комбинация
               (напр. 4.00м -> сървърът връща 4.00/1.80, а URL оставаше с
               междинния 3.50/1.80) - синхронизираме след отговора. */
            if (
              nikaSelectedVariant?.id &&
              this.dataset.templateProductMatch === 'true' &&
              !this.closest('product-card') &&
              !this.closest('quick-add-dialog')
            ) {
              const nikaUrl = new URL(window.location.href);
              if (nikaUrl.searchParams.get('variant') !== String(nikaSelectedVariant.id)) {
                nikaUrl.searchParams.set('variant', String(nikaSelectedVariant.id));
                history.replaceState({}, '', nikaUrl.toString());
              }
              nikaSyncCanonical(String(nikaSelectedVariant.id));
            }

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
/**
 * NIKA: Chrome (Share this page / Android share sheet) споделя каноничния адрес
 * (<link rel="canonical">), не този от адресната лента — затова линкът към
 * избран размер пристигаше без ?variant=. След избор на вариант синхронизираме
 * и canonical. Първоначалният HTML остава без параметър (SEO не се пипа —
 * Googlebot не кликва по размерите).
 * @param {string} variantId
 */
function nikaSyncCanonical(variantId) {
  const link = document.querySelector('link[rel="canonical"]');
  if (!link) return;
  try {
    const canonical = new URL(link.href, window.location.origin);
    if (variantId) canonical.searchParams.set('variant', variantId);
    else canonical.searchParams.delete('variant');
    if (link.href !== canonical.toString()) link.href = canonical.toString();
    const og = document.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute('content', canonical.toString());
  } catch {
    /* невалиден canonical — оставяме го */
  }
}

